import type { ContentIssue } from './types';

/** Схемы, которые допускаются в режиме «Ссылка». */
export const ALLOWED_URL_SCHEMES = ['http:', 'https:'] as const;

/** Схемы, которые не принимаем: исполняемые или бессмысленные для QR. */
const DANGEROUS_SCHEMES = ['javascript:', 'data:', 'file:', 'vbscript:', 'blob:'];

const SCHEME_RE = /^([a-zA-Z][a-zA-Z0-9+.-]*):/;

export type UrlParse =
  | { status: 'ok'; url: URL; host: string }
  | { status: 'no-scheme'; suggestion: string }
  | { status: 'dangerous-scheme'; scheme: string }
  | { status: 'foreign-scheme'; scheme: string }
  | { status: 'invalid' };

/**
 * Разбирает ввод в режиме ссылки. Ничего не переписывает молча:
 * отсутствующая схема возвращается как предложение, а не как результат.
 */
export function parseUrlInput(raw: string): UrlParse {
  const value = raw.trim();
  if (value === '') return { status: 'invalid' };

  const schemeMatch = SCHEME_RE.exec(value);
  if (!schemeMatch) {
    // Без схемы: предлагаем https://, но только как явное действие пользователя.
    const candidate = `https://${value}`;
    try {
      const probe = new URL(candidate);
      if (!probe.hostname || !probe.hostname.includes('.')) return { status: 'invalid' };
      return { status: 'no-scheme', suggestion: candidate };
    } catch {
      return { status: 'invalid' };
    }
  }

  const scheme = schemeMatch[1]!.toLowerCase() + ':';
  if (DANGEROUS_SCHEMES.includes(scheme)) return { status: 'dangerous-scheme', scheme };
  if (!(ALLOWED_URL_SCHEMES as readonly string[]).includes(scheme)) {
    return { status: 'foreign-scheme', scheme };
  }

  try {
    const url = new URL(value);
    if (!url.hostname) return { status: 'invalid' };
    return { status: 'ok', url, host: url.hostname };
  } catch {
    return { status: 'invalid' };
  }
}

/**
 * Проверяет, переживёт ли строка сериализацию через URL без изменений.
 * Если нет — это повод предупредить, а не тихо переписать ввод.
 */
export function urlRoundTrips(raw: string): boolean {
  try {
    return new URL(raw.trim()).toString() === raw.trim();
  } catch {
    return false;
  }
}

export function urlIssues(raw: string): { issues: ContentIssue[]; host: string | null } {
  const parsed = parseUrlInput(raw);
  const issues: ContentIssue[] = [];

  switch (parsed.status) {
    case 'ok': {
      if (!urlRoundTrips(raw)) {
        issues.push({
          level: 'info',
          code: 'url-normalizable',
          message:
            'Браузер записал бы этот адрес немного иначе. В QR попадёт ровно введённая строка — так безопаснее для подписанных ссылок.',
        });
      }
      if (parsed.url.protocol === 'http:') {
        issues.push({
          level: 'warning',
          code: 'url-insecure',
          message: 'Адрес использует http:// без шифрования. Проверьте, доступна ли https-версия.',
        });
      }
      return { issues, host: parsed.host };
    }
    case 'no-scheme':
      issues.push({
        level: 'error',
        code: 'url-no-scheme',
        message: 'В адресе нет протокола. Ничего не добавляем автоматически.',
        fix: { label: 'Добавить https://', value: parsed.suggestion },
      });
      return { issues, host: null };
    case 'dangerous-scheme':
      issues.push({
        level: 'error',
        code: 'url-dangerous-scheme',
        message: `Схема ${parsed.scheme} не принимается в режиме «Ссылка». Если это нужно закодировать буквально, переключитесь на «Текст».`,
      });
      return { issues, host: null };
    case 'foreign-scheme':
      issues.push({
        level: 'error',
        code: 'url-foreign-scheme',
        message: `Схема ${parsed.scheme} не поддерживается в первой версии. Допустимы http:// и https://.`,
      });
      return { issues, host: null };
    default:
      issues.push({
        level: 'error',
        code: 'url-invalid',
        message: 'Не удалось разобрать адрес. Проверьте домен и синтаксис.',
      });
      return { issues, host: null };
  }
}
